/**
 * CList Component
 *
 * A unified list component with three display variants for different use cases.
 * Each variant has its own visual design. Use inside MainCard for consistent styling.
 *
 * Variants:
 *   - `activity` — Icon-based items with badges and points (for tasks, achievements, challenges)
 *   - `avatar` — Initials-based items with timestamps (for team members, users, contacts)
 *   - `status` — Left-bordered items with status badges (for orders, tickets, requests)
 *
 * Props:
 *   - `variant: "activity" | "avatar" | "status"` — Determines which list design to render (required)
 *   - `items: ActivityItemType[] | AvatarItemType[] | StatusItemType[]` — Array of items (type depends on variant)
 *   - `className?: string` — Additional CSS classes for the container
 *   - `showDivider?: boolean` — Show dividers between items (default: true)
 *
 * Item Types:
 *
 *   ActivityItemType (variant="activity"):
 *     - `id: string` — Unique identifier
 *     - `title: string` — Item title
 *     - `subtitle?: string` — Description or timestamp
 *     - `badge: { text: string; variant: ActivityBadgeVariant }` — Badge config
 *     - `points?: number` — Points value (hidden if 0 or undefined)
 *     - `pointsColor?: string` — Override points text color
 *     - `icon?: ReactNode` — Custom icon component
 *     - `iconKey?: string` — Predefined icon key (star, trophy, graduation, trending, heart, calendar, users, zap, check)
 *     - `iconBgColor: string` — Background color for icon container
 *     - `iconColor: string` — Icon color
 *
 *   AvatarItemType (variant="avatar"):
 *     - `id?: string` — Unique identifier
 *     - `title: string` — Person/item name
 *     - `subtitle?: string` — Role or description
 *     - `initials: string` — 1-2 character initials for avatar
 *     - `initialsColor?: string` — Avatar background/text color (default: "bg-primary/10 text-primary")
 *     - `badge?: { label: string; variant?: AvatarBadgeVariant }` — Optional status badge
 *     - `timestamp?: string` — Time indicator (e.g., "2 hours ago")
 *     - `onClick?: () => void` — Click handler for the item
 *
 *   StatusItemType (variant="status"):
 *     - `id?: string` — Unique identifier
 *     - `title: string` — Item title
 *     - `subtitle?: string` — Description
 *     - `status: StatusVariant` — Status type (urgent, pending, new, success, warning, danger, info)
 *     - `statusLabel: string` — Text shown in status badge
 *     - `onClick?: () => void` — Click handler for the item
 *
 * Examples:
 *
 * ```tsx
 * // Activity variant inside MainCard
 * <MainCard title="Challenges & Tasks" subtitle="Active goals">
 *   <CList
 *     variant="activity"
 *     items={[
 *       {
 *         id: "1",
 *         title: "Sales Champion",
 *         subtitle: "Top seller gets 100 pts",
 *         badge: { text: "WEEKLY", variant: "amber" },
 *         points: 100,
 *         iconKey: "trophy",
 *         iconBgColor: "bg-amber-100 dark:bg-amber-900/30",
 *         iconColor: "text-amber-600 dark:text-amber-400",
 *       },
 *     ]}
 *   />
 * </MainCard>
 * ```
 *
 * ```tsx
 * // Avatar variant inside MainCard
 * <MainCard title="Team Members" subtitle="Active staff">
 *   <CList
 *     variant="avatar"
 *     items={[
 *       {
 *         id: "1",
 *         title: "John Smith",
 *         subtitle: "Senior Developer",
 *         initials: "JS",
 *         badge: { label: "Online", variant: "success" },
 *         timestamp: "2 hours ago",
 *       },
 *     ]}
 *   />
 * </MainCard>
 * ```
 *
 * ```tsx
 * // Status variant inside MainCard
 * <MainCard title="Recent Orders">
 *   <CList
 *     variant="status"
 *     items={[
 *       {
 *         id: "1",
 *         title: "Order #12345",
 *         subtitle: "2 items - $49.99",
 *         status: "pending",
 *         statusLabel: "Processing",
 *         onClick: () => openOrder("12345"),
 *       },
 *     ]}
 *   />
 * </MainCard>
 * ```
 *
 * Notes:
 *   - Always use inside MainCard for consistent card styling across the app
 *   - Use `ACTIVITY_ICON_STYLES` constant for consistent icon colors with dark mode support
 *   - TypeScript enforces correct item type based on variant (discriminated union)
 */
"use client";

import React from "react";
import { cn } from "@/lib/utils";
import ActivityListItem from "./ActivityListItem";
import AvatarListItem from "./AvatarListItem";
import StatusListItem from "./StatusListItem";
import type { CListProps } from "./types";

const CList: React.FC<CListProps> = (props) => {
  const { className, showDivider = true } = props;

  // Render the appropriate list based on variant
  const renderListContent = () => {
    switch (props.variant) {
      case "activity":
        return (
          <ActivityListItem items={props.items} showDivider={showDivider} />
        );
      case "avatar":
        return <AvatarListItem items={props.items} showDivider={showDivider} />;
      case "status":
        return <StatusListItem items={props.items} showDivider={showDivider} />;
      default:
        return null;
    }
  };

  return <div className={cn(className)}>{renderListContent()}</div>;
};

export default CList;
